import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Paperclip, Send, X } from 'lucide-react';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Skeleton, SkeletonRow } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { Button } from '@shared/ui/primitives/Button';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import { formatDateTime, formatRelative } from '@shared/lib/format';
import { cn } from '@shared/lib/cn';
import { platform } from '@shared/lib/platform';
import { toast } from '@shared/ui/feedback/Toast';
import {
  fetchSupportRequest,
  postSupportReply,
  uploadSupportCommentAttachment,
  type SupportMessage,
} from './api';

/** Sits above TabBar (`fixed bottom-0`) + safe area. */
const TAB_BAR_OFFSET = 'calc(4.25rem + env(safe-area-inset-bottom, 0px))';

const STATUS: Record<string, { label: string; tone: string }> = {
  open: { label: 'Open', tone: 'bg-brass/25 text-brass' },
  in_progress: { label: 'In progress', tone: 'bg-white/15 text-white' },
  awaiting_member: { label: 'Awaiting you', tone: 'bg-brass/25 text-brass' },
  resolved: { label: 'Resolved', tone: 'bg-white/12 text-white/70' },
  closed: { label: 'Closed', tone: 'bg-white/12 text-white/60' },
};

// Note: text-warning / text-danger are branded CSS-var colours; only solid
// text colours are used (no /opacity, which silently renders transparent).
const PRIORITY_META: Record<string, { label: string; dot: string; text: string }> = {
  low: { label: 'Low priority', dot: 'bg-ink-muted', text: 'text-ink-secondary' },
  normal: { label: 'Normal priority', dot: 'bg-ink-muted', text: 'text-ink-secondary' },
  high: { label: 'High priority', dot: 'bg-warning', text: 'text-warning' },
  urgent: { label: 'Urgent', dot: 'bg-danger', text: 'text-danger' },
};

/**
 * Support request thread — fixed chat layout: scrollable messages and a
 * composer pinned above the bottom tab bar.
 */
export function SupportDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const requestQuery = useQuery({
    queryKey: ['support-request', id],
    queryFn: () => fetchSupportRequest(id ?? ''),
    enabled: !!id,
  });

  // Auto-scroll the thread to the newest comment when the request opens / a
  // message arrives. rAF lets the list lay out first; { block: 'end' } jumps
  // (no animation) so it's already at the bottom on open.
  const bottomRef = useRef<HTMLDivElement>(null);
  const loadedRequestId = requestQuery.data?.request_id;
  const messageCount = requestQuery.data?.messages?.length ?? 0;
  useEffect(() => {
    if (!loadedRequestId) return;
    const frame = requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ block: 'end' });
    });
    return () => cancelAnimationFrame(frame);
  }, [loadedRequestId, messageCount]);

  if (requestQuery.isLoading) {
    return (
      <div className="bg-paper px-5 pt-safe-or-6" style={{ paddingBottom: TAB_BAR_OFFSET }}>
        <Skeleton className="h-32 rounded-2xl" />
        <div className="mt-4 flex flex-col gap-3">
          <SkeletonRow count={3} />
        </div>
      </div>
    );
  }

  if (requestQuery.isError) {
    return (
      <div className="bg-paper px-5 pt-safe-or-6 pb-safe-or-6">
        <button
          type="button"
          onClick={() => navigate('/app/support/requests')}
          className="mb-4 text-body-sm font-medium text-primary-700"
        >
          ← Back
        </button>
        <ErrorState onRetry={() => requestQuery.refetch()} />
      </div>
    );
  }

  const request = requestQuery.data;
  if (!request) {
    return (
      <div className="bg-paper px-5 pt-safe-or-6 pb-safe-or-6">
        <EmptyState title="Request not found." />
      </div>
    );
  }

  const status = STATUS[request.status] ?? {
    label: request.status,
    tone: 'bg-white/15 text-white/90',
  };
  const priorityMeta = request.priority ? PRIORITY_META[request.priority] : undefined;

  const thread: SupportMessage[] = [
    {
      message_id: `opening-${request.request_id}`,
      author: 'member',
      author_name: 'You',
      body: request.description,
      created_at: request.created_at,
    },
    ...request.messages,
  ];

  return (
    <div
      className="-mb-24 flex flex-col overflow-hidden bg-paper"
      style={{
        // Full viewport height; `paddingBottom` reserves space for the fixed
        // TabBar so the composer pins directly above it. (Subtracting the top
        // inset here left a gap of that size between the composer and TabBar.)
        height: '100dvh',
        paddingBottom: TAB_BAR_OFFSET,
      }}
    >
      <DetailHeader
        onBack={() => navigate('/app/support/requests')}
        eyebrow="Support"
        title={request.subject}
        subtitle={
          <>
            {request.topic_name ? <span>{request.topic_name}</span> : null}
            {request.topic_name ? <span aria-hidden> · </span> : null}
            <span>Opened {formatRelative(request.created_at)}</span>
          </>
        }
        badge={status.label}
        badgeTone={status.tone}
      />

      <div className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain px-5 pt-4 pb-4">
        {priorityMeta ? (
          <div className="mb-4 flex items-center">
            <span
              className={cn(
                'inline-flex items-center gap-1.5 rounded-pill bg-sunken px-3 py-1 text-caption font-medium',
                priorityMeta.text,
              )}
            >
              <span className={cn('h-1.5 w-1.5 rounded-pill', priorityMeta.dot)} />
              {priorityMeta.label}
            </span>
          </div>
        ) : null}

        {request.attachments && request.attachments.length > 0 ? (
          <div className="mb-4 grid grid-cols-3 gap-2">
            {request.attachments.map((a) => (
              <a
                key={a.id}
                href={a.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block aspect-square overflow-hidden rounded-md hairline"
              >
                {a.mime.startsWith('image/') ? (
                  <img src={a.url} alt={a.name} className="h-full w-full object-cover" />
                ) : (
                  <span className="flex h-full items-center justify-center px-2 text-center text-caption text-ink-muted">
                    {a.name}
                  </span>
                )}
              </a>
            ))}
          </div>
        ) : null}

        <section className="flex min-w-0 flex-col gap-4">
          {thread.map((msg, index) => (
            <MessageBubble
              key={msg.message_id}
              msg={msg}
              showOpeningLabel={index === 0}
            />
          ))}
          <div ref={bottomRef} aria-hidden />
        </section>
      </div>

      <Composer requestId={request.request_id} />
    </div>
  );
}

function MessageBubble({
  msg,
  showOpeningLabel,
}: {
  msg: SupportMessage;
  showOpeningLabel?: boolean;
}): JSX.Element {
  const isMember = msg.author === 'member';
  const isSystem = msg.author === 'system';

  return (
    <article
      className={cn(
        'flex min-w-0 flex-col gap-1',
        isSystem ? 'items-center' : isMember ? 'items-end' : 'items-start',
      )}
    >
      <div
        className={cn(
          'max-w-[88%] min-w-0 break-words whitespace-pre-line rounded-2xl px-4 py-3 text-body leading-relaxed',
          isMember &&
            'rounded-br-md bg-gradient-to-br from-[var(--primary-600)] via-[var(--primary-700)] to-[var(--primary-800)] text-white shadow-sm',
          !isMember &&
            !isSystem &&
            'rounded-bl-md bg-[color:var(--surface-sunken)] text-ink',
          isSystem && 'max-w-full rounded-xl bg-sunken/80 px-3 py-2 text-center text-body-sm italic text-ink-secondary',
        )}
      >
        {msg.body}
      </div>
      {msg.attachments && msg.attachments.length > 0 ? (
        <div
          className={cn(
            'flex max-w-[88%] flex-wrap gap-2',
            isMember ? 'justify-end' : 'justify-start',
          )}
        >
          {msg.attachments.map((a) =>
            a.mime.startsWith('image/') ? (
              <a
                key={a.id}
                href={a.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block overflow-hidden rounded-xl hairline"
              >
                <img src={a.url} alt={a.name} className="max-h-48 w-auto max-w-full object-cover" />
              </a>
            ) : (
              <a
                key={a.id}
                href={a.url}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-lg bg-sunken px-3 py-2 text-caption text-ink-secondary hairline"
              >
                {a.name}
              </a>
            ),
          )}
        </div>
      ) : null}
      {!isSystem ? (
        <span
          className={cn(
            'max-w-[88%] text-caption text-ink-muted',
            isMember ? 'text-right' : 'text-left',
          )}
        >
          {showOpeningLabel ? 'Your request · ' : null}
          {isMember ? 'You' : msg.author_name ?? 'Support'} · {formatDateTime(msg.created_at)}
        </span>
      ) : null}
    </article>
  );
}

function DetailHeader({
  onBack,
  eyebrow,
  title,
  subtitle,
  badge,
  badgeTone,
}: {
  onBack: () => void;
  eyebrow: string;
  title: string;
  subtitle?: ReactNode;
  badge?: string;
  badgeTone?: string;
}): JSX.Element {
  return (
    <HeroHeader
      className="shrink-0"
      onBack={onBack}
      backLabel="Back to my requests"
      eyebrow={eyebrow}
      eyebrowSuffix={
        badge ? (
          <span
            className={cn(
              'rounded-pill px-2.5 py-0.5 text-[0.625rem] font-semibold uppercase tracking-wide',
              badgeTone ?? 'bg-white/15 text-white/90',
            )}
          >
            {badge}
          </span>
        ) : null
      }
      title={title}
      subtitle={subtitle}
      titleClassName="mt-2 leading-tight"
    />
  );
}

function Composer({ requestId }: { requestId: string }): JSX.Element {
  const [body, setBody] = useState('');
  const [attachment, setAttachment] = useState<{ blob: Blob; preview: string } | null>(null);
  const [attaching, setAttaching] = useState(false);
  const queryClient = useQueryClient();

  const clearAttachment = () =>
    setAttachment((prev) => {
      if (prev) URL.revokeObjectURL(prev.preview);
      return null;
    });

  const mutation = useMutation({
    mutationFn: async () => {
      // comment_text is required (min 1) — fall back to a marker for photo-only.
      const text = body.trim() || '📷 Photo';
      const message = await postSupportReply(requestId, text);
      if (attachment) {
        await uploadSupportCommentAttachment(message.message_id, attachment.blob);
      }
      return message;
    },
    onSuccess: () => {
      setBody('');
      clearAttachment();
      queryClient.invalidateQueries({ queryKey: ['support-request', requestId] });
      queryClient.invalidateQueries({ queryKey: ['support-requests'] });
    },
    onError: (e) => toast.danger("Couldn't send", e instanceof Error ? e.message : undefined),
  });

  const canSend = (body.trim().length > 0 || !!attachment) && !mutation.isPending;

  const submit = () => {
    if (!canSend) return;
    mutation.mutate();
  };

  const pickPhoto = async () => {
    if (attaching) return;
    setAttaching(true);
    try {
      let blob: Blob;
      if (platform.isNative()) {
        // CameraSource.Prompt → iOS sheet offering Camera or Photo Library.
        const shot = await Camera.getPhoto({
          quality: 80,
          resultType: CameraResultType.Uri,
          source: CameraSource.Prompt,
        });
        if (!shot.webPath) return;
        blob = await fetch(shot.webPath).then((r) => r.blob());
      } else {
        const file = await new Promise<File | null>((resolve) => {
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = 'image/*';
          input.onchange = () => resolve(input.files?.[0] ?? null);
          input.click();
        });
        if (!file) return;
        blob = file;
      }
      setAttachment((prev) => {
        if (prev) URL.revokeObjectURL(prev.preview);
        return { blob, preview: URL.createObjectURL(blob) };
      });
    } catch (e) {
      toast.danger("Couldn't add photo", e instanceof Error ? e.message : undefined);
    } finally {
      setAttaching(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      className="shrink-0 border-t border-[color:var(--hairline)] bg-paper px-5 pt-3 pb-3 shadow-[0_-4px_24px_rgba(0,0,0,0.06)]"
    >
      {attachment ? (
        <div className="mb-2 flex items-center gap-2">
          <div className="relative">
            <img
              src={attachment.preview}
              alt="Attachment preview"
              className="h-16 w-16 rounded-lg object-cover hairline"
            />
            <button
              type="button"
              onClick={clearAttachment}
              aria-label="Remove photo"
              className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-pill bg-ink text-white"
            >
              <X className="h-3 w-3" strokeWidth={2} />
            </button>
          </div>
        </div>
      ) : null}
      <div className="flex items-end gap-2">
        <button
          type="button"
          onClick={() => void pickPhoto()}
          disabled={attaching}
          aria-label="Add photo"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-pill bg-[color:var(--surface-sunken)] text-ink-secondary disabled:opacity-60"
        >
          <Paperclip className="h-5 w-5" strokeWidth={1.75} />
        </button>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Reply…"
          rows={1}
          className={cn(
            'min-h-11 max-h-32 flex-1 resize-none rounded-2xl bg-[color:var(--surface-sunken)]',
            'px-4 py-2.5 text-body leading-5 text-ink',
            'border border-transparent focus:border-primary-500 focus:bg-card focus:outline-none',
          )}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <Button
          type="submit"
          size="icon"
          className="h-11 w-11 shrink-0"
          loading={mutation.isPending}
          disabled={!canSend}
          aria-label="Send reply"
        >
          <Send className="h-4 w-4" strokeWidth={1.5} />
        </Button>
      </div>
    </form>
  );
}
